import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../css-dashboard/Dashboard.css";
import {
  useJsApiLoader,
  GoogleMap,
  DirectionsRenderer,
} from "@react-google-maps/api";
import UseReceiverTripsNegotiations from "../../hooks/UseTripsNegotiations/UseReceiverTripsNegotiations";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const defaultCenter = { lat: -15.7801, lng: -47.9292 };
const mapId = "DEMO_MAP_ID";

function buildStoredStop(stored, prefix) {
  const name = stored?.[`cidade_${prefix}`] || "";
  const lat = Number(stored?.[`lat_cidade_${prefix}`]);
  const lng = Number(stored?.[`lng_cidade_${prefix}`]);

  if (!name || Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    name,
    position: { lat, lng },
  };
}

function toRad(value) {
  return (value * Math.PI) / 180;
}

function calculateDistanceKm(pointA, pointB) {
  if (!pointA || !pointB) return 0;

  const R = 6371;
  const dLat = toRad(pointB.lat - pointA.lat);
  const dLng = toRad(pointB.lng - pointA.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(pointA.lat)) *
      Math.cos(toRad(pointB.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getBoundsPaddingByDistance(distanceKm) {
  if (distanceKm <= 5) return 50;
  if (distanceKm <= 20) return 70;
  if (distanceKm <= 100) return 90;
  if (distanceKm <= 500) return 110;
  return 130;
}

export default function TripTracking() {
  const location = useLocation();
  const navigate = useNavigate();

  const mapRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const hasFitBoundsRef = useRef(false);
  const lastDestinationKeyRef = useRef("");

  const storedTrip = location.state?.item?.trip || null;
  const serieTrip = location.state?.item?.serie_trip_negotiations;

  const [currentPos, setCurrentPos] = useState(null);
  const [destinationPos, setDestinationPos] = useState(null);
  const [directions, setDirections] = useState(null);

  const {
    tripsNegotiations: tripsNegotiationsReceiver,
    refetchTripsNegotiations: refetchTripsNegotiationsReceiver,
  } = UseReceiverTripsNegotiations();

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    language: "pt-BR",
    region: "BR",
  });

  const tripFinded = useMemo(() => {
    return tripsNegotiationsReceiver.find(
      (trip) => trip.serie_trip_negotiations === serieTrip,
    );
  }, [tripsNegotiationsReceiver, serieTrip]);

  const travelerLat = tripFinded?.traveler?.lat;
  const travelerLng = tripFinded?.traveler?.lng;

  const mapOptions = useMemo(
    () => ({
      mapId,
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
    }),
    [],
  );

  const directionsOptions = useMemo(
    () => ({
      routeIndex: 0,
      suppressMarkers: true,
      preserveViewport: true,
      polylineOptions: {
        strokeColor: "#2563eb",
        strokeWeight: 5,
        strokeOpacity: 0.92,
      },
    }),
    [],
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      refetchTripsNegotiationsReceiver();
    }, 2000);

    return () => clearInterval(intervalId);
  }, [refetchTripsNegotiationsReceiver]);

  useEffect(() => {
    if (!isLoaded || !storedTrip) return;

    const restoredDestination = buildStoredStop(storedTrip, "destino");

    const originFromTraveler =
      travelerLat != null && travelerLng != null
        ? {
            lat: Number(travelerLat),
            lng: Number(travelerLng),
          }
        : null;

    if (originFromTraveler) {
      setCurrentPos((prev) => {
        if (
          prev &&
          prev.lat === originFromTraveler.lat &&
          prev.lng === originFromTraveler.lng
        ) {
          return prev;
        }
        return originFromTraveler;
      });
    }

    if (restoredDestination?.position) {
      setDestinationPos((prev) => {
        if (
          prev &&
          prev.lat === restoredDestination.position.lat &&
          prev.lng === restoredDestination.position.lng
        ) {
          return prev;
        }
        return restoredDestination.position;
      });
    }
  }, [isLoaded, storedTrip, travelerLat, travelerLng]);

  useEffect(() => {
    if (!currentPos || !destinationPos || !window.google) return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: currentPos,
        destination: destinationPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
      },
      (result, status) => {
        if (status === "OK" && result?.routes?.length) {
          setDirections(result);
        } else {
          console.error("Erro ao gerar rota:", status);
          setDirections(null);
        }
      },
    );
  }, [currentPos, destinationPos]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const map = mapRef.current;

    if (currentPos) {
      if (!currentMarkerRef.current) {
        currentMarkerRef.current = new window.google.maps.Marker({
          map,
          position: currentPos,
          title: "Localização atual",
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: "#2563eb",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
            scale: 8,
          },
          zIndex: 9999,
        });
      } else {
        currentMarkerRef.current.setPosition(currentPos);
      }
    }

    if (destinationPos) {
      if (!destinationMarkerRef.current) {
        destinationMarkerRef.current = new window.google.maps.Marker({
          map,
          position: destinationPos,
          title: "Destino",
          label: {
            text: "B",
            color: "#ffffff",
            fontWeight: "700",
            fontSize: "14px",
          },
        });
      } else {
        destinationMarkerRef.current.setPosition(destinationPos);
      }
    }
  }, [isLoaded, currentPos, destinationPos]);

  useEffect(() => {
    if (!destinationPos || !currentPos) return;

    const destinationKey = `${destinationPos.lat},${destinationPos.lng}`;
    if (lastDestinationKeyRef.current !== destinationKey) {
      lastDestinationKeyRef.current = destinationKey;
      hasFitBoundsRef.current = false;
    }
  }, [destinationPos, currentPos]);

  useEffect(() => {
    if (
      !isLoaded ||
      !mapRef.current ||
      !window.google ||
      !currentPos ||
      !destinationPos
    ) {
      return;
    }

    const map = mapRef.current;
    const distanceKm = calculateDistanceKm(currentPos, destinationPos);
    const padding = getBoundsPaddingByDistance(distanceKm);

    if (!hasFitBoundsRef.current) {
      if (directions?.routes?.[0]?.bounds) {
        map.fitBounds(directions.routes[0].bounds, padding);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(currentPos);
        bounds.extend(destinationPos);
        map.fitBounds(bounds, padding);
      }

      hasFitBoundsRef.current = true;
      return;
    }

    map.panTo(currentPos);
  }, [isLoaded, currentPos, destinationPos, directions]);

  useEffect(() => {
    return () => {
      if (currentMarkerRef.current) {
        currentMarkerRef.current.setMap(null);
        currentMarkerRef.current = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.setMap(null);
        destinationMarkerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="payment-dashboard-container fullscreen-map-page">
      <style>{`
        :root {
          --bg-main: #f6f8fc;
          --bg-card: #ffffff;
          --text-main: #0f172a;
          --text-muted: #64748b;
          --primary: #2563eb;
          --primary-2: #0f7cff;
          --accent: #06b6d4;
          --accent-hover: #0891b2;
          --border-color: #e2e8f0;
          --radius-lg: 26px;
          --radius-md: 16px;
          --radius-sm: 12px;
          --shadow-sm: 0 6px 18px rgba(15, 23, 42, 0.06);
          --shadow-md: 0 12px 28px rgba(15, 23, 42, 0.10);
          --shadow-lg: 0 24px 50px rgba(15, 23, 42, 0.12);
        }

        html, body, #root {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
        }

        .payment-dashboard-container {
          position: relative;
          color: var(--text-main);
          min-height: 100vh;
          width: 100%;
          padding: 0;
          box-sizing: border-box;
          overflow: hidden;
          background: #0f172a;
        }

        .payment-dashboard-container * {
          box-sizing: border-box;
        }

        .max-width-wrapper {
          width: 100%;
          height: 100vh;
          margin: 0;
          display: block;
          position: relative;
        }

        .dashboard-header {
          position: absolute;
          top: 20px;
          left: 50%;
          z-index: 20;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          min-height: 70px;
          background: rgba(255, 255, 255, 0.88);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1px solid rgba(255, 255, 255, 0.65);
          border-radius: 20px;
          box-shadow: 0 14px 40px rgba(15, 23, 42, 0.16);
        }

        .back-button {
          width: 44px;
          height: 44px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
          box-shadow: 0 10px 24px rgba(37, 99, 235, 0.28);
          flex-shrink: 0;
        }

        .back-button:hover {
          transform: translateY(-1px);
          filter: brightness(1.03);
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.34);
        }

        .back-button:active {
          transform: translateY(0);
        }

        .header-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .header-badge {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #2563eb;
        }

        .dashboard-header h1 {
          margin: 0;
          font-size: 18px;
          line-height: 1.2;
          font-weight: 700;
          color: #0f172a;
          white-space: nowrap;
        }

        .main-workspace-grid {
          width: 100%;
          height: 100vh;
          display: block;
        }

        .map-section-wrapper {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          z-index: 1;
        }

        .map-container-frame {
          width: 100%;
          height: 100%;
          background: var(--bg-card);
          border-radius: 0;
          border: none;
          overflow: hidden;
          box-shadow: none;
        }

        .map-placeholder-loading {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          background: #f1f5f9;
        }

        .current-location-marker {
          position: relative;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .current-location-marker__pulse {
          position: absolute;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(37, 99, 235, 0.25);
          animation: pulse 1.6s ease-out infinite;
        }

        .current-location-marker__dot {
          position: relative;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2563eb;
          border: 3px solid #ffffff;
          box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.2);
          z-index: 1;
        }

        .destination-marker {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #16a34a;
          color: #ffffff;
          font-weight: 800;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.22);
          border: 2px solid #ffffff;
        }

        @keyframes pulse {
          0% {
            transform: scale(0.7);
            opacity: 0.8;
          }
          70% {
            transform: scale(1.5);
            opacity: 0.12;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }

        @media (max-width: 720px) {
          .dashboard-header {
            top: 14px;
            padding: 12px 14px;
            margin-top: 60px;
            gap: 12px;
            border-radius: 18px;
          }

          .back-button {
            width: 40px;
            height: 40px;
            border-radius: 12px;
          }

          .dashboard-header h1 {
            font-size: 16px;
            white-space: normal;
          }

          .header-badge {
            font-size: 10px;
          }
        }
      `}</style>

      <div className="page max-width-wrapper">
        <div className="dashboard-header">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="header-content">
            <span className="header-badge">Viagem</span>
            <h1>Acompanhamento em Tempo Real</h1>
          </div>
        </div>

        <div className="main-workspace-grid">
          <div className="map-section-wrapper">
            <div className="map-container-frame">
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{
                    width: "100%",
                    height: "100%",
                  }}
                  zoom={10}
                  center={defaultCenter}
                  options={mapOptions}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                >
                  {directions && (
                    <DirectionsRenderer
                      directions={directions}
                      options={directionsOptions}
                    />
                  )}
                </GoogleMap>
              ) : (
                <div className="map-placeholder-loading">
                  Carregando mapa interativo...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
